import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Button,
  Card,
} from '@mui/material';
import {
  Schedule as ClockIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useSubscription } from '../../contexts/SubscriptionContext';

export const TrialStatusBanner: React.FC = () => {
  const { theme } = useTheme();
  const { subscription } = useSubscription();
  const navigate = useNavigate();

  if (!subscription || subscription.status !== 'trialing' || !subscription.trialEnd) {
    return null;
  }

  const now = new Date();
  const trialEnd = new Date(subscription.trialEnd);

  const totalDays = 30;
  const daysRemaining = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  const daysUsed = totalDays - daysRemaining;
  const progress = (daysUsed / totalDays) * 100;

  if (daysRemaining <= 0) {
    return null;
  }

  return (
    <Card
      elevation={2}
      sx={{
        background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, #E06B7D 100%)`,
        borderRadius: theme.radii.medium,
        p: 1.5,
        mb: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ClockIcon sx={{ color: 'white', fontSize: 18 }} />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: 'white',
                fontSize: '0.85rem',
              }}
            >
              Período Gratuito
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.9)',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              px: 1.25,
              py: 0.4,
              borderRadius: theme.radii.small,
              fontSize: '0.7rem',
            }}
          >
            {daysRemaining} {daysRemaining === 1 ? 'dia' : 'dias'}
          </Typography>
        </Box>

        <Box sx={{ mb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 5,
              borderRadius: theme.radii.small,
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'white',
                borderRadius: theme.radii.small,
              },
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '0.7rem',
            }}
          >
            Plano Basic ativo
          </Typography>
          <Button
            size="small"
            onClick={() => navigate('/pricing')}
            sx={{
              backgroundColor: 'white',
              color: theme.colors.secondary,
              fontWeight: 700,
              fontSize: '0.7rem',
              borderRadius: theme.radii.small,
              px: 1.5,
              py: 0.5,
              textTransform: 'none',
              minHeight: 0,
              height: 24,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              },
            }}
          >
            Ver Planos
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          zIndex: 1,
        }}
      />
    </Card>
  );
};
